import { type FC } from "react";
import { Link, generatePath } from "react-router-dom";

import {
    Button,
    DropdownInput,
    Option,
    Radio,
    RadioGroup,
    TooltipIndicator,
    TooltipPlacementTypes,
} from "@snapchat/snap-design-system";
import { useIntl } from "react-intl";

import { AbsoluteRoute } from "../../core/routes";
import { useV3Routes, useWebLensRoutes } from "../../core/routes/hooks";
import { PageHeader } from "../../layout/components";
import { useWebLensFolderSelection } from "../hooks/useWebLensFolderSelection";
import { useWebLensStartMode } from "../hooks/useWebLensStartMode";
import { WEB_LENS_CREATE_MODE, WEB_LENS_PUSH_MODE, WEB_LENS_UPDATE_MODE, isWebLensStartMode } from "../model";

import styles from "./WebLensPage.module.css";
import { WebLensUpdatePickerList } from "./WebLensUpdatePickerList";
import {
    ContinueCta,
    CreateFolderLink,
    CreateOrganizationLink,
    FolderLabel,
    GoToLensFoldersCta,
    LoadOrganizationsFailed,
    ModeCreateOption,
    ModePushOption,
    ModeUpdateOption,
    NoFoldersInOrganization,
    NoOrganizations,
    OrganizationNotAccessible,
    PushModeFolderHint,
    StartTitle,
    UpdateNoFoldersInOrganization,
    messages,
} from "./messages.generated";

/**
 * Step 1 of the web-lens flow, forked by mode: submit a new lens (default — pick the Lens Space
 * it is created in, mirroring the lens-submission flow's select-folder step), update an existing
 * one (the same folder choice scopes a searchable lens list; picking a lens opens the update
 * page), or push a development build to the creator's own Snapchat for a quick preview (no folder
 * at all — the preview is ephemeral and never lands in a Lens Space). Keeping the fork here keeps
 * the one-way "becomes a web lens" update action inside the web-lens flow instead of on every
 * lens page. The organization is not chosen on this page — it comes from the route, switched via
 * the app-level org menu (top right), like every other org-scoped feature. Folder and mode ride
 * the query string, so they survive reloads.
 */
export const WebLensStartPage: FC = () => {
    const intl = useIntl();
    const { goToWebLensSubmit, goToWebLensPushToDevice } = useWebLensRoutes();
    const { goToLensFolders } = useV3Routes();
    const { loading, failed, organizations, selectedOrganization, selectedFolder, selectFolder } =
        useWebLensFolderSelection();
    const { mode, setMode } = useWebLensStartMode();
    const isUpdateMode = mode === "update";
    const isPushMode = mode === "push";

    const showNoOrganizations = !loading && !failed && organizations.length === 0;
    // The org comes from the route, switched via the app-level org menu (top right) — this page
    // deliberately has no selector of its own (review feedback: two selectors for one choice read
    // as two choices). A route org the creator has no access to gets pointed at that menu.
    const showOrganizationNotAccessible = !loading && !failed && organizations.length > 0 && !selectedOrganization;
    // Non-null exactly when the picked org has no folders — carries the org for the CTA link.
    const folderlessOrganization = selectedOrganization?.folders.length === 0 ? selectedOrganization : null;

    // No Blizzard event here: Continue is pure navigation, and the next page's view is
    // auto-tracked (telemetry doc); the business events are web_lens_submit and
    // web_lens_push_to_device on their pages.
    const onContinue = () => {
        if (!selectedOrganization) {
            return;
        }
        if (isPushMode) {
            goToWebLensPushToDevice(selectedOrganization.id);
            return;
        }
        if (!selectedFolder) {
            return;
        }
        goToWebLensSubmit(selectedOrganization.id, selectedFolder.id);
    };

    return (
        <div className={styles.page}>
            <PageHeader>
                <StartTitle />
            </PageHeader>

            <div className={styles.chooser}>
                <div className={styles.chooserSection} data-testid="web-lens-mode">
                    <RadioGroup
                        horizontal
                        value={mode}
                        onChange={(value: string) => {
                            if (isWebLensStartMode(value)) {
                                setMode(value);
                            }
                        }}
                    >
                        <Radio label={<ModeCreateOption />} value={WEB_LENS_CREATE_MODE} />
                        <Radio label={<ModeUpdateOption />} value={WEB_LENS_UPDATE_MODE} />
                        <Radio label={<ModePushOption />} value={WEB_LENS_PUSH_MODE} />
                    </RadioGroup>
                </div>

                {/* Push mode needs no destination: the preview is ephemeral and never lands in a
                    Lens Space, so the folder chooser gives way to a line saying exactly that. */}
                {isPushMode ? (
                    <div className={styles.chooserHint} data-testid="web-lens-push-mode-hint">
                        <PushModeFolderHint />
                    </div>
                ) : (
                    <div className={styles.chooserSection}>
                        <div className={styles.sectionLabelRow}>
                            <div className={styles.sectionLabel}>
                                <FolderLabel />
                            </div>
                            <TooltipIndicator
                                content={intl.formatMessage(messages.folderTooltip)}
                                placement={TooltipPlacementTypes.TOP}
                            />
                        </div>
                        <DropdownInput
                            placeholder={intl.formatMessage(messages.folderPlaceholder)}
                            onChange={selectFolder}
                            disabled={loading || !selectedOrganization}
                            loading={loading}
                            value={selectedFolder?.id}
                            data-testid="web-lens-select-folder"
                        >
                            {(selectedOrganization?.folders ?? []).map((folder) => (
                                <Option key={folder.id} value={folder.id}>
                                    {folder.name}
                                </Option>
                            ))}
                        </DropdownInput>
                    </div>
                )}

                {/* Folders are created on the org's Lens Folders page — the same destination as the
                    folderless-org CTA below, offered here as the quiet path for everyone else.
                    Update mode hides it: a brand-new folder has no lenses to update. */}
                {selectedOrganization && !isUpdateMode && !isPushMode ? (
                    <div className={styles.createFolderRow}>
                        <Link
                            to={generatePath(AbsoluteRoute.ORG_LENS_FOLDERS, { orgId: selectedOrganization.id })}
                            className={styles.textLink}
                        >
                            <CreateFolderLink />
                        </Link>
                    </div>
                ) : null}
                {/* No Lens Central org (the route org is a Business / Camera Kit one; with no org at
                    all AppRoutes sends creators to Welcome): same create-org page as the Lens Studio
                    flow, which returns here under the new org. A new org has no lenses to update. */}
                {showNoOrganizations && !isUpdateMode ? (
                    <div className={styles.createFolderRow}>
                        <Link
                            to={generatePath(AbsoluteRoute.CREATE_ORG_LENS_FOLDER, {
                                routeTo: "web-lens",
                                submissionId: null,
                            })}
                            className={styles.textLink}
                            data-testid="web-lens-create-organization"
                        >
                            <CreateOrganizationLink />
                        </Link>
                    </div>
                ) : null}

                {failed ? (
                    <div className={styles.chooserError}>
                        <LoadOrganizationsFailed />
                    </div>
                ) : null}
                {showNoOrganizations ? (
                    <div className={styles.chooserError}>
                        <NoOrganizations />
                    </div>
                ) : null}
                {showOrganizationNotAccessible ? (
                    <div className={styles.chooserError} data-testid="web-lens-org-not-accessible">
                        <OrganizationNotAccessible />
                    </div>
                ) : null}
                {/* A folderless org is only a problem when a folder is the destination — a push
                    has none, so the hint (and the Lens Folders CTA below) stays out of its way. */}
                {folderlessOrganization && !isPushMode ? (
                    <div className={styles.chooserHint}>
                        {isUpdateMode ? <UpdateNoFoldersInOrganization /> : <NoFoldersInOrganization />}
                    </div>
                ) : null}
            </div>

            {isUpdateMode ? (
                selectedOrganization && selectedFolder ? (
                    <WebLensUpdatePickerList orgId={selectedOrganization.id} folderId={selectedFolder.id} />
                ) : null
            ) : (
                <div className={styles.actions}>
                    {/* Product decision: same as regular lens submission — a lens folder is required,
                        so an org without folders routes the creator to Lens Folders to create one.
                        Not in push mode, where no folder is involved. */}
                    {folderlessOrganization && !isPushMode ? (
                        <Button
                            type="default"
                            onClick={() => {
                                goToLensFolders(folderlessOrganization.id);
                            }}
                            data-testid="web-lens-go-to-lens-folders"
                        >
                            <GoToLensFoldersCta />
                        </Button>
                    ) : null}
                    <Button
                        type="primary"
                        isDisabled={!selectedOrganization || (!isPushMode && !selectedFolder)}
                        onClick={onContinue}
                        data-testid="web-lens-folder-continue"
                    >
                        <ContinueCta />
                    </Button>
                </div>
            )}
        </div>
    );
};
